import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPenToSquare, faTrash, faXmark, faArrowLeft, faEye } from '@fortawesome/free-solid-svg-icons';
import Navigation from '../../../Layouts/Navigation/Navigation';
import { BASE_URL } from '../../../utils/constants';
import useAutoDismissSnack from '../../../Hooks/AutoDismissSnack';

// Icono de mascota personalizado como SVG
const PetIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <path d="M8.5 3C7.12 3 6 4.12 6 5.5S7.12 8 8.5 8 11 6.88 11 5.5 9.88 3 8.5 3zm7 0C14.12 3 13 4.12 13 5.5S14.12 8 15.5 8 18 6.88 18 5.5 16.88 3 15.5 3zM3 9.25c-1.24 0-2.25 1.01-2.25 2.25S1.76 13.75 3 13.75 5.25 12.74 5.25 11.5 4.24 9.25 3 9.25zm18 0c-1.24 0-2.25 1.01-2.25 2.25s1.01 2.25 2.25 2.25S23.25 12.74 23.25 11.5 22.24 9.25 21 9.25zM12 10c-3.31 0-6 2.69-6 6 0 1.66.67 3.16 1.76 4.24l1.42-1.42C8.45 18.09 8 17.1 8 16c0-2.21 1.79-4 4-4s4 1.79 4 4c0 1.1-.45 2.09-1.18 2.82l1.42 1.42C17.33 19.16 18 17.66 18 16c0-3.31-2.69-6-6-6z"/>
  </svg>
);

function ControlPets() {
  const { id_torre } = useParams();
  const navigate = useNavigate();
  const [pets, setPets] = useState([]);
  const [filteredPets, setFilteredPets] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [towers, setTowers] = useState([]);
  const [apartments, setApartments] = useState([]);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [open, setOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [selectedPet, setSelectedPet] = useState(null);
  const [viewOpen, setViewOpen] = useState(false);
  const [viewPet, setViewPet] = useState(null);

  // Form states
  const [nombre, setNombre] = useState("");
  const [tipo, setTipo] = useState("PERRO");
  const [raza, setRaza] = useState("");
  const [telefono, setTelefono] = useState("");
  const [selectedTower, setSelectedTower] = useState("");
  const [selectedApartment, setSelectedApartment] = useState("");
  const [selectedUser, setSelectedUser] = useState("");

  // Snack notifications
  const [openSnack, setOpenSnack] = useState(false);
  const [snackStatus, setSnackStatus] = useState("");
  const [snackMessage, setSnackMessage] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
    getPets();
    getTowers();
  }, [id_torre]);

  const handleClickSnack = () => {
    setOpenSnack(true);
  };

  const handleCloseSnack = () => {
    setOpenSnack(false);
  };
  useAutoDismissSnack(openSnack, setOpenSnack);

  const getTowers = () => {
    axios.get(`${BASE_URL}/towers`)
      .then(response => setTowers(response.data))
      .catch(error => console.error('Error fetching towers:', error));
  };

  const getPets = () => {
    axios.get(`${BASE_URL}/pets/tower/${id_torre}`)
      .then(response => {
        setPets(response.data);
        setFilteredPets(response.data);
      })
      .catch(error => console.error('Error fetching pets:', error));
  };

  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchTerm(value);
    const filtered = pets.filter((pet) =>
      pet.nombre.toLowerCase().includes(value) ||
      pet.tipo?.toLowerCase().includes(value) ||
      pet.raza.toLowerCase().includes(value) ||
      pet.usuario_nombre?.toLowerCase().includes(value) ||
      pet.usuario_apellido?.toLowerCase().includes(value) ||
      pet.num_apartamento?.toString().includes(value)
    );
    setFilteredPets(filtered);
    setPage(0);
  };

  const handleClickOpen = () => {
    setOpen(true);
    loadApartments(id_torre);
  };

  const handleClose = () => {
    setOpen(false);
    setNombre("");
    setTipo("PERRO");
    setRaza("");
    setTelefono("");
    setSelectedApartment("");
    setSelectedUser("");
    setApartments([]);
    setUsers([]);
  };

  const loadApartments = (towerId) => {
    axios.get(`${BASE_URL}/apartments/tower/${towerId}`)
      .then(response => setApartments(response.data))
      .catch(error => console.error('Error fetching apartments:', error));
  };

  const loadUsers = (apartmentId) => {
    axios.get(`${BASE_URL}/users/apartment/${apartmentId}`)
      .then(response => setUsers(response.data))
      .catch(error => console.error('Error fetching users:', error));
  };

  const handleTowerChange = (towerId) => {
    setSelectedTower(towerId);
    setSelectedApartment("");
    setSelectedUser("");
    setTelefono("");
    setUsers([]);
    if (towerId) {
      loadApartments(towerId);
    } else {
      setApartments([]);
    }
  };

  const handleApartmentChange = (apartmentId) => {
    setSelectedApartment(apartmentId);
    setSelectedUser("");
    setTelefono("");
    if (apartmentId) {
      loadUsers(apartmentId);
    } else {
      setUsers([]);
    }
  };

  const handleUserChange = (userId) => {
    setSelectedUser(userId);
    const user = users.find(u => u.id_usuario === parseInt(userId));
    if (user && user.telefono) {
      setTelefono(user.telefono);
    }
  };

  const handleAddSubmit = (event) => {
    event.preventDefault();

    const petData = {
      nombre: nombre,
      tipo: tipo,
      raza: raza,
      telefono: telefono,
      id_torre: id_torre,
      id_apto: selectedApartment,
      id_usuario: selectedUser
    };

    axios.post(`${BASE_URL}/addPet`, petData)
      .then(response => {
        if (response.status === 200) {
          setSnackStatus("success");
          setSnackMessage("Mascota agregada correctamente");
          getPets();
          handleClose();
        }
        handleClickSnack();
      })
      .catch(error => {
        console.error('Error adding pet:', error);
        setSnackStatus("error");
        setSnackMessage(error.response?.data?.error || "Error al agregar la mascota");
        handleClickSnack();
      });
  };

  const handleEditPet = (pet) => {
    setSelectedPet(pet);
    setTipo(pet.tipo || "PERRO");
    setEditOpen(true);
    setSelectedTower(pet.id_torre);
    loadApartments(pet.id_torre);
    
    // Cargar apartamento y usuarios
    setTimeout(() => {
      setSelectedApartment(pet.id_apto);
      loadUsers(pet.id_apto);
    }, 500);
  };

  const handleEditSubmit = (event) => {
    event.preventDefault();

    const petData = {
      nombre: selectedPet.nombre,
      tipo: selectedPet.tipo,
      raza: selectedPet.raza,
      telefono: selectedPet.telefono,
      id_torre: selectedPet.id_torre,
      id_apto: selectedPet.id_apto,
      id_usuario: selectedPet.id_usuario
    };

    axios.put(`${BASE_URL}/updatePet/${selectedPet.id_mascota}`, petData)
      .then(response => {
        setSnackStatus("success");
        setSnackMessage("Mascota actualizada correctamente");
        getPets();
        setEditOpen(false);
        handleClickSnack();
      })
      .catch(error => {
        console.error('Error updating pet:', error);
        setSnackStatus("error");
        setSnackMessage(error.response?.data?.error || "Error al actualizar la mascota");
        handleClickSnack();
      });
  };

  const handleDeletePet = (id_mascota) => {
    if (window.confirm('¿Está seguro de eliminar esta mascota?')) {
      axios.delete(`${BASE_URL}/deletePet/${id_mascota}`)
        .then(response => {
          setSnackStatus("success");
          setSnackMessage("Mascota eliminada correctamente");
          getPets();
          handleClickSnack();
        })
        .catch(error => {
          console.error('Error deleting pet:', error);
          setSnackStatus("error");
          setSnackMessage("Error al eliminar la mascota");
          handleClickSnack();
        });
    }
  };

  return (
    <>
      <Navigation />
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4 sm:p-6 lg:p-8">
        <div className="max-w-7xl mx-auto">
          {/* Back Button */}
          <button
            onClick={() => navigate('/adminPets')}
            className="mb-6 inline-flex items-center gap-2 px-4 py-2 bg-white text-gray-700 rounded-lg hover:bg-gray-100 transition-colors shadow-md"
          >
            <FontAwesomeIcon icon={faArrowLeft} className="h-5 w-5" />
            Volver
          </button>

          {/* Snackbar Alert */}
          {openSnack && (
            <div className={`fixed top-4 right-4 p-4 rounded-lg shadow-2xl ${
              snackStatus === 'success' ? 'bg-green-500' : 'bg-red-500'
            } text-white animate-fade-in-down`} style={{ zIndex: 9999 }}>
              <div className="flex items-center gap-2">
                <span className="font-semibold">{snackMessage}</span>
                <button onClick={handleCloseSnack} className="ml-2 hover:bg-white/20 rounded p-1 transition-colors">
                  <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                </button>
              </div>
            </div>
          )}

          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-center gap-3 mb-6">
              <PetIcon className="h-10 w-10 text-green-600" />
              <h1 className="text-4xl font-bold text-gray-800">Mascotas de la Torre {id_torre}</h1>
            </div>

            {/* Search and Add Button */}
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between mb-6">
              <div className="flex-1 w-full sm:max-w-md">
                <input
                  type="text"
                  placeholder="Buscar por nombre, raza, propietario o apartamento..."
                  value={searchTerm}
                  onChange={handleSearch}
                  className="input-field w-full"
                />
              </div>
              <button
                onClick={handleClickOpen}
                className="btn-primary whitespace-nowrap"
              >
                + Agregar Mascota
              </button>
            </div>
          </div>

          {/* Add Pet Modal */}
          {open && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 1000 }}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 rounded-t-2xl">
                  <h2 className="text-2xl font-bold text-white">Agregar Nueva Mascota</h2>
                  <p className="text-green-100 mt-1">Complete los datos de la mascota</p>
                </div>
                <form onSubmit={handleAddSubmit}>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre de la Mascota *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={nombre}
                          onChange={(e) => setNombre(e.target.value.toUpperCase())}
                          required
                          autoFocus
                          placeholder="FIRULAIS, MICHI, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tipo *
                        </label>
                        <select
                          className="input-field"
                          value={tipo}
                          onChange={(e) => setTipo(e.target.value)}
                          required
                        >
                          <option value="PERRO">Perro</option>
                          <option value="GATO">Gato</option>
                          <option value="AVE">Ave</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Raza *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={raza}
                          onChange={(e) => setRaza(e.target.value.toUpperCase())}
                          required
                          placeholder="GOLDEN RETRIEVER, PERSA, etc."
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Apartamento *
                        </label>
                        <select
                          className="input-field"
                          value={selectedApartment}
                          onChange={(e) => handleApartmentChange(e.target.value)}
                          required
                        >
                          <option value="">Seleccione un apartamento</option>
                          {apartments.map((apt) => (
                            <option key={apt.id_apartamento} value={apt.id_apartamento}>
                              Apto {apt.num_apartamento}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Propietario *
                        </label>
                        <select
                          className="input-field"
                          value={selectedUser}
                          onChange={(e) => handleUserChange(e.target.value)}
                          required
                          disabled={!selectedApartment}
                        >
                          <option value="">Seleccione un propietario</option>
                          {users.map((user) => (
                            <option key={user.id_usuario} value={user.id_usuario}>
                              {user.nombre} {user.apellido}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Teléfono del Propietario
                        </label>
                        <input
                          type="tel"
                          className="input-field"
                          value={telefono}
                          onChange={(e) => setTelefono(e.target.value)}
                          placeholder="3001234567"
                          maxLength="20"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={handleClose}
                      className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Agregar Mascota
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Edit Pet Modal */}
          {editOpen && selectedPet && (
            <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4" style={{ zIndex: 1000 }}>
              <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="sticky top-0 bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 rounded-t-2xl">
                  <h2 className="text-2xl font-bold text-white">Editar Mascota</h2>
                  <p className="text-green-100 mt-1">Modifique los datos de la mascota</p>
                </div>
                <form onSubmit={handleEditSubmit}>
                  <div className="p-6 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Nombre de la Mascota *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={selectedPet.nombre}
                          onChange={(e) => setSelectedPet({...selectedPet, nombre: e.target.value.toUpperCase()})}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Tipo *
                        </label>
                        <select
                          className="input-field"
                          value={selectedPet.tipo}
                          onChange={(e) => setSelectedPet({...selectedPet, tipo: e.target.value})}
                          required
                        >
                          <option value="PERRO">Perro</option>
                          <option value="GATO">Gato</option>
                          <option value="AVE">Ave</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Raza *
                        </label>
                        <input
                          type="text"
                          className="input-field"
                          value={selectedPet.raza}
                          onChange={(e) => setSelectedPet({...selectedPet, raza: e.target.value.toUpperCase()})}
                          required
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Torre *
                        </label>
                        <select
                          className="input-field"
                          value={selectedPet.id_torre}
                          onChange={(e) => {
                            setSelectedPet({...selectedPet, id_torre: e.target.value, id_apto: '', id_usuario: ''});
                            loadApartments(e.target.value);
                          }}
                          required
                        >
                          <option value="">Seleccione una torre</option>
                          {towers.map((tower) => (
                            <option key={tower.id_torre} value={tower.id_torre}>
                              {tower.nombre}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Apartamento *
                        </label>
                        <select
                          className="input-field"
                          value={selectedPet.id_apto}
                          onChange={(e) => {
                            setSelectedPet({...selectedPet, id_apto: e.target.value, id_usuario: ''});
                            loadUsers(e.target.value);
                          }}
                          required
                        >
                          <option value="">Seleccione un apartamento</option>
                          {apartments.map((apt) => (
                            <option key={apt.id_apartamento} value={apt.id_apartamento}>
                              Apto {apt.num_apartamento}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Propietario *
                        </label>
                        <select
                          className="input-field"
                          value={selectedPet.id_usuario}
                          onChange={(e) => {
                            const user = users.find(u => u.id_usuario === parseInt(e.target.value));
                            setSelectedPet({
                              ...selectedPet, 
                              id_usuario: e.target.value,
                              telefono: user?.telefono || selectedPet.telefono
                            });
                          }}
                          required
                        >
                          <option value="">Seleccione un propietario</option>
                          {users.map((user) => (
                            <option key={user.id_usuario} value={user.id_usuario}>
                              {user.nombre} {user.apellido}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Teléfono del Propietario
                        </label>
                        <input
                          type="tel"
                          className="input-field"
                          value={selectedPet.telefono || ''}
                          onChange={(e) => setSelectedPet({...selectedPet, telefono: e.target.value})}
                          placeholder="3001234567"
                          maxLength="20"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="sticky bottom-0 bg-gray-50 px-6 py-4 rounded-b-2xl flex justify-end gap-3 border-t border-gray-200">
                    <button
                      type="button"
                      onClick={() => setEditOpen(false)}
                      className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                    >
                      Cancelar
                    </button>
                    <button
                      type="submit"
                      className="btn-primary"
                    >
                      Guardar Cambios
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-md overflow-hidden">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gradient-to-r from-green-500 to-emerald-600">
                  <tr>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Nombre</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Tipo</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Raza</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Teléfono</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Apartamento</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Propietario</th>
                    <th className="px-6 py-4 text-center text-sm font-bold text-white uppercase tracking-wider">Acciones</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPets
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((pet) => (
                      <tr key={pet.id_mascota} className="hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-4 text-center text-sm font-bold text-gray-900">{pet.nombre}</td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">{pet.tipo}</td>
                        <td className="px-6 py-4 text-center text-sm text-gray-700">{pet.raza}</td>
                        <td className="px-6 py-4 text-center text-sm text-blue-600 font-medium">
                          {pet.telefono || '-'}
                        </td>
                        <td className="px-6 py-4 text-center text-sm font-medium text-gray-900">
                          {pet.num_apartamento}
                        </td>
                        <td className="px-6 py-4 text-center text-sm text-gray-900">
                          {pet.usuario_nombre} {pet.usuario_apellido}
                        </td>
                        <td className="px-6 py-4 text-center">
                          <div className="flex justify-center gap-2">
                            <button
                              onClick={() => { setViewPet(pet); setViewOpen(true); }}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors shadow-sm hover:shadow-md"
                              title="Ver mascota"
                            >
                              <FontAwesomeIcon icon={faEye} className="h-5 w-5" />
                              Ver
                            </button>
                            <button
                              onClick={() => handleEditPet(pet)}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm hover:shadow-md"
                              title="Editar mascota"
                            >
                              <FontAwesomeIcon icon={faPenToSquare} className="h-5 w-5" />
                              Editar
                            </button>
                            <button
                              onClick={() => handleDeletePet(pet.id_mascota)}
                              className="inline-flex items-center gap-2 px-3 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors shadow-sm hover:shadow-md"
                              title="Eliminar mascota"
                            >
                              <FontAwesomeIcon icon={faTrash} className="h-5 w-5" />
                              Eliminar
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-700">Filas por página:</span>
                <select
                  value={rowsPerPage}
                  onChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                  }}
                  className="input-field py-1"
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={100}>100</option>
                </select>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-sm text-gray-700">
                  {page * rowsPerPage + 1}-{Math.min((page + 1) * rowsPerPage, filteredPets.length)} de {filteredPets.length}
                </span>
                <div className="flex gap-2">
                  <button
                    onClick={() => setPage(page - 1)}
                    disabled={page === 0}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Anterior
                  </button>
                  <button
                    onClick={() => setPage(page + 1)}
                    disabled={page >= Math.ceil(filteredPets.length / rowsPerPage) - 1}
                    className="px-3 py-1 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* View Pet Dialog */}
          {viewOpen && viewPet && (
            <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-[1000] p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
                <div className="bg-gradient-to-r from-green-500 to-emerald-600 px-6 py-4 rounded-t-2xl flex items-center justify-between">
                  <h2 className="text-xl font-bold text-white">Detalle de Mascota</h2>
                  <button onClick={() => setViewOpen(false)} className="text-white/80 hover:text-white transition-colors">
                    <FontAwesomeIcon icon={faXmark} className="h-5 w-5" />
                  </button>
                </div>
                <div className="p-6">
                  {/* Foto */}
                  <div className="flex justify-center mb-5">
                    {viewPet.foto ? (
                      <img
                        src={`${BASE_URL}${viewPet.foto}`}
                        alt={viewPet.nombre}
                        className="w-40 h-40 rounded-2xl object-cover shadow-md border-4 border-green-100"
                      />
                    ) : (
                      <div className="w-40 h-40 rounded-2xl bg-green-50 border-4 border-green-100 flex items-center justify-center">
                        <PetIcon className="h-20 w-20 text-green-300" />
                      </div>
                    )}
                  </div>
                  {/* Info */}
                  <div className="space-y-3">
                    {[
                      { label: 'Nombre', value: viewPet.nombre },
                      { label: 'Tipo', value: viewPet.tipo },
                      { label: 'Raza', value: viewPet.raza },
                      { label: 'Propietario', value: `${viewPet.usuario_nombre} ${viewPet.usuario_apellido}` },
                      { label: 'Apartamento', value: viewPet.num_apartamento },
                      { label: 'Teléfono', value: viewPet.telefono || '—' },
                    ].map(({ label, value }) => (
                      <div key={label} className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-gray-500 w-28 shrink-0">{label}:</span>
                        <span className="text-sm text-gray-800 font-medium">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="bg-gray-50 px-6 py-4 rounded-b-2xl border-t border-gray-200 flex justify-end">
                  <button
                    onClick={() => setViewOpen(false)}
                    className="px-6 py-2.5 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors font-medium"
                  >
                    Cerrar
                  </button>
                </div>
              </div>
            </div>
          )}

          {filteredPets.length === 0 && (
            <div className="text-center py-12 bg-white rounded-2xl shadow-md mt-4">
              <PetIcon className="h-24 w-24 text-gray-300 mx-auto mb-4" />
              <p className="text-xl text-gray-500">No hay mascotas registradas</p>
              <p className="text-sm text-gray-400 mt-2">Haz clic en "Agregar Mascota" para registrar la primera</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default ControlPets;


